import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { createContext, useContext, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import Skeleton from '@/components/ui/Skeleton'
import TextLink from '@/components/ui/TextLink'
import TitleRow from '@/components/ui/TitleRow'
import { useSuspenseFundStockCount } from '@/features/mutual-funds/api/queries'
import { fundLinkOptions } from '@/features/mutual-funds/lib/links'
import { clampPage } from '@/features/mutual-funds/lib/defaults'
import { formatQuantity } from '@/lib/format'
import { cmp } from '@/lib/money'
import type { FundStockCount } from '@/schemas/mutual-funds'
import FundSectorsDialog from '@/screens/mutual-funds/components/analytics/FundSectorsDialog'
import { sliceTablePage, toPageSize } from '@/schemas/table'
import {
  FUND_COLUMN_WIDTH,
  FUND_SORT,
  FUND_TABLE,
  fundLeadColumn,
  moneyColumn,
} from '@/screens/mutual-funds/components/shared/fund-table'
import {
  loadRankings,
  rankingSortSchema,
  rankingsSearchSchema,
  type RankingSort,
} from '@/screens/mutual-funds/RankingsScreen.loader'

const TITLE = 'Fund Rankings'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title={TITLE} />

/**
 * `rankingsSearchSchema`'s own `pageSize` default (20) as the row count. A
 * table under that size (a narrower pageSize, the last page) is shorter than
 * this skeleton, never taller.
 */
const SKELETON_ROWS = 20

/**
 * The loaded page's one block: the 280 search, 16 over the table (three columns — Fund, Stocks,
 * Portfolio value — at the two-line row height) and `Pagination`'s own row 24 under it.
 */
function RankingsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading fund rankings…" header={HEADER} pageTitle={TITLE}>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-11 w-full rounded-sm sm:w-70 lg:h-9" />
        <div>
          <DataTableSkeleton rows={SKELETON_ROWS} columns={3} rowHeight="lg" />
          <div className="mt-6 flex h-11 items-center justify-between gap-3 lg:h-8">
            <Skeleton className="h-4 w-36 rounded-xs" />
            <Skeleton className="h-4 w-44 rounded-xs" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

function RankingsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Fund rankings did not load" />
}

export const Route = createFileRoute('/_app/mutual-funds/rankings')({
  validateSearch: rankingsSearchSchema,
  loader: loadRankings,
  pendingComponent: RankingsPending,
  errorComponent: RankingsError,
  component: RankingsScreen,
})

/**
 * The mock's columns (`#/funds`): the fund taking the room, then Stocks (160) and the portfolio
 * value (140) at fixed widths against the right edge. "Portfolio value", not the mock's "AUM"
 * (owner default Q3): it is the analytics `value`, never the directory's `aum`.
 */
/** Opens one fund's sector dialog, reaching the cells through context so `COLUMNS` stays put. */
const OpenFund = createContext<(symbol: string) => void>(() => undefined)

/**
 * The mock's gold count (`mf-count`): a button in the row that opens the fund's split, the dialog
 * Analytics opens, which lists the fund's largest holdings under its sectors once
 * `/mutual-funds/{fund}/holdings/` is live (mm-be #372).
 */
function StocksCell({ row }: { row: FundStockCount }) {
  const onOpen = useContext(OpenFund)
  return (
    <TextLink
      aria-haspopup="dialog"
      aria-label={`${formatQuantity(row.stockCount)}, see the sectors ${row.symbol} holds`}
      onClick={() => {
        onOpen(row.symbol)
      }}
      className="tnum"
    >
      {formatQuantity(row.stockCount)}
    </TextLink>
  )
}

const COLUMNS: readonly DataTableColumn<FundStockCount>[] = [
  fundLeadColumn<FundStockCount>(),
  {
    id: 'stocks',
    accessorFn: (row) => row.stockCount,
    header: 'Stocks',
    ...FUND_SORT,
    meta: { numeric: true, width: FUND_COLUMN_WIDTH.w160 },
    cell: ({ row }) => <StocksCell row={row.original} />,
  },
  moneyColumn<FundStockCount>({
    id: 'value',
    header: 'Portfolio value',
    value: (row) => row.value,
    width: FUND_COLUMN_WIDTH.w140,
  }),
]

/**
 * The whole list, in the order the member asked for — before it is paged, so a sort reorders the
 * funds and not just one page of them. No sort is the opening view: most stocks held first, with
 * no head marked (the mock's). A stable sort: funds that tie keep the backend's own order. A fund
 * with no value sorts last whichever way the column runs.
 */
const sortFunds = (
  funds: readonly FundStockCount[],
  sortBy: RankingSort | undefined,
  desc: boolean,
): FundStockCount[] => {
  const direction = desc ? -1 : 1
  return [...funds].sort((a, b) => {
    if (sortBy === undefined) return b.stockCount - a.stockCount
    if (sortBy === 'stocks') return direction * (a.stockCount - b.stockCount)
    if (a.value === null) return b.value === null ? 0 : 1
    if (b.value === null) return -1
    return direction * cmp(a.value, b.value)
  })
}

/**
 * Mutual funds · Fund rankings — every fund in the latest monthly filings,
 * by how many different stocks it holds or what that portfolio is worth.
 *
 * THE PATTERN THE OTHER FIVE SCREENS COPY: the loader awaits the main read and
 * the screen reads it with suspense — its data is always defined, and a
 * failed background refetch keeps it. (A main read keyed on a search param —
 * Price vs NAV's category, a holdings period — is the plain hook with
 * `keepPreviousData` instead; see docs/route-loading.md, "Search params".)
 * What the URL holds here is only the sort and the page, applied client-side,
 * since the whole list arrives in one response.
 *
 * No rank column: funds that tie share a rank, and once the table sorts by
 * value a number beside each name stops meaning anything. "Portfolio value" is
 * the analytics `value` — what the fund's reported holdings are worth — never
 * the directory's `aum` (see `fundSchema`).
 */
/** The search box's match: a symbol or a name, case-blind (the mock's "Search a fund or a symbol"). */
const matchesFund = (row: FundStockCount, needle: string): boolean =>
  needle === '' ||
  row.symbol.toLowerCase().includes(needle) ||
  (row.name?.toLowerCase().includes(needle) ?? false)

function RankingsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: funds } = useSuspenseFundStockCount(null)
  const [term, setTerm] = useState('')
  const [openFund, setOpenFund] = useState<string | undefined>(undefined)

  const needle = term.trim().toLowerCase()
  const sortDesc = search.sortDesc ?? true
  const sorted = sortFunds(
    funds.filter((row) => matchesFund(row, needle)),
    search.sortBy,
    sortDesc,
  )
  const page = sliceTablePage(sorted, {
    page: clampPage(search.page, sorted.length, search.pageSize),
    pageSize: search.pageSize,
  })

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      <div className="flex flex-col gap-4">
        <SearchInput
          aria-label="Find a fund"
          placeholder="Search a fund or a symbol"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value)
            if (search.page !== 1) void navigate({ search: (prev) => ({ ...prev, page: 1 }) })
          }}
          onClear={() => {
            setTerm('')
          }}
          className="w-full px-3 sm:w-70"
        />
        <OpenFund value={setOpenFund}>
          <DataTable
            {...FUND_TABLE}
            columns={COLUMNS}
            rows={page.rows}
            getRowId={(row) => row.symbol}
            rowLinkOptions={(row) => fundLinkOptions(row.symbol)}
            caption="Mutual funds by the number of stocks held and the value of their portfolio"
            sortBy={search.sortBy}
            sortDesc={sortDesc}
            onSortChange={(next, desc) => {
              // A third click on the same head clears the sort and comes back as no column: the
              // opening view, written as no params at all so it stays the same URL.
              const column = rankingSortSchema.safeParse(next)
              void navigate({
                search: (prev) => ({
                  ...prev,
                  sortBy: column.success ? column.data : undefined,
                  sortDesc: column.success ? desc : undefined,
                  page: 1,
                }),
              })
            }}
            emptyState={
              needle !== '' ? (
                <EmptyState kind="no-results" query={term.trim()} />
              ) : (
                <EmptyState
                  title="No fund holdings reported yet"
                  description="Funds file their holdings monthly. The ranking fills in once a month’s filings are in."
                />
              )
            }
          />
        </OpenFund>
        {sorted.length === 0 ? null : (
          <Pagination
            page={page.page}
            pageSize={page.pageSize}
            total={page.total}
            unit="funds"
            onPageChange={(next) => {
              void navigate({ search: (prev) => ({ ...prev, page: next }) })
            }}
            onPageSizeChange={(next) => {
              void navigate({
                search: (prev) => ({ ...prev, pageSize: toPageSize(next, prev.pageSize), page: 1 }),
              })
            }}
          />
        )}
      </div>
      <FundSectorsDialog
        fund={openFund}
        onClose={() => {
          setOpenFund(undefined)
        }}
      />
    </ScreenLayout>
  )
}
